import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ProviderManager() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [editingProvider, setEditingProvider] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    founded: '',
    specialization: '',
    status: 'Active and Verified',
    barcodeId: '',
    rating: '',
    completedProjects: '',
    location: '',
    email: '',
    phone: '',
    website: '',
    verifiedSince: '',
    paymentDate: '',
    subscriptionDuration: ''
  });

  // Helper function to calculate subscription details
  const calculateSubscriptionDetails = (paymentDate, subscriptionDuration) => {
    if (!paymentDate || !subscriptionDuration) {
      return { expiryDate: null, remainingDays: null };
    }
    const payment = new Date(paymentDate);
    const expiry = new Date(payment);
    expiry.setFullYear(expiry.getFullYear() + parseInt(subscriptionDuration));
    const today = new Date();
    const timeDiff = expiry.getTime() - today.getTime();
    const remainingDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return {
      expiryDate: expiry.toLocaleDateString('en-GB'),
      remainingDays: remainingDays
    };
  };

  // Load providers from API
  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/admin/providers`, { headers });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      } else {
        console.error('Failed to fetch providers');
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProvider(null);
    setFormData({
      id: `REG-2024-${String(providers.length + 1).padStart(3, '0')}`,
      name: '',
      founded: '',
      specialization: '',
      status: 'Active and Verified',
      barcodeId: `BC-${String(providers.length + 1).padStart(3, '0')}-2024`,
      rating: '',
      completedProjects: '',
      location: '',
      email: '',
      phone: '',
      website: '',
      verifiedSince: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      paymentDate: '',
      subscriptionDuration: ''
    });
    setShowForm(true);
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider.id);
    setFormData(provider);
    setShowForm(true);
  };

  const handleToggleVisibility = async (provider) => {
    const willHide = !provider.isHidden;
    if (!window.confirm(willHide
      ? 'Hide this provider from the public Verified Providers list?'
      : 'Show this provider on the public Verified Providers list?'
    )) return;

    try {
      const token = localStorage.getItem('adminToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/providers/${provider.id}/visibility`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ hidden: willHide })
      });

      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        return;
      }
      const result = await response.json();
      if (response.ok && result.success) {
        fetchProviders();
      } else {
        alert(result.message || 'Failed to update visibility');
      }
    } catch (error) {
      alert('Error updating visibility');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this provider?')) {
      try {
        const token = localStorage.getItem('adminToken');
        const headers = {
          'Content-Type': 'application/json'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/providers/${id}`, {
          method: 'DELETE',
          headers
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        const result = await response.json();

        if (response.ok && result.success) {
          setProviders(prev => prev.filter(p => p.id !== id));
          alert('Provider deleted successfully!');
        } else {
          alert(`Failed to delete provider: ${result.message || 'Unknown error'}`);
          console.error('Failed to delete provider:', result);
        }
      } catch (error) {
        alert(`Error deleting provider: ${error.message}`);
        console.error('Error deleting provider:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (editingProvider) {
        // Update existing provider
        const response = await fetch(`${API_URL}/providers/${editingProvider}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData),
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        if (response.ok) {
          fetchProviders(); // Reload providers
          setShowForm(false);
          setEditingProvider(null);
        } else {
          console.error('Failed to update provider');
        }
      } else {
        // Add new provider
        const response = await fetch(`${API_URL}/providers`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formData),
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        if (response.ok) {
          fetchProviders(); // Reload providers
          setShowForm(false);
        } else {
          console.error('Failed to add provider');
        }
      }
    } catch (error) {
      console.error('Error submitting provider:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProvider(null);
  };

  const handleViewDetails = (provider) => {
    setSelectedProvider(provider);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedProvider(null);
  };

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ margin: 0 }}>Verified Providers Management</h2>
        <button
          onClick={handleAdd}
          className="submit-btn"
          style={{ backgroundColor: '#27ae60' }}
        >
          + Add New Provider
        </button>
      </div>

      {showForm && (
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '30px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: '2px solid #3498db'
        }}>
          <h3 style={{ marginTop: 0, color: '#2c3e50' }}>
            {editingProvider ? 'Edit Provider' : 'Add New Provider'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Provider Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Founded Year</label>
                <input
                  type="text"
                  value={formData.founded}
                  onChange={(e) => setFormData({...formData, founded: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Specialization *</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="Active and Verified">Active and Verified</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>

              <div className="form-group">
                <label>Rating</label>
                <input
                  type="text"
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: e.target.value})}
                  placeholder="e.g., 4.5/5"
                />
              </div>

              <div className="form-group">
                <label>Completed Projects</label>
                <input
                  type="text"
                  value={formData.completedProjects}
                  onChange={(e) => setFormData({...formData, completedProjects: e.target.value})}
                  placeholder="e.g., 150+"
                />
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Payment Date (Subscription Active Date)</label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Subscription Duration</label>
                <select
                  value={formData.subscriptionDuration}
                  onChange={(e) => setFormData({...formData, subscriptionDuration: e.target.value})}
                >
                  <option value="">No Subscription</option>
                  <option value="1">1 Year</option>
                  <option value="2">2 Years</option>
                  <option value="3">3 Years</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="submit-btn" style={{ backgroundColor: '#27ae60' }}>
                {editingProvider ? 'Update Provider' : 'Add Provider'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="submit-btn"
                style={{ backgroundColor: '#95a5a6' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by ID, name, specialization, location, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 15px',
            fontSize: '14px',
            border: '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
          }}
          onFocus={(e) => e.target.style.borderColor = '#3498db'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
        {searchQuery && !loading && (
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            Showing {providers.filter(provider => {
              const query = searchQuery.toLowerCase();
              return (
                provider.id.toLowerCase().includes(query) ||
                provider.name.toLowerCase().includes(query) ||
                provider.specialization.toLowerCase().includes(query) ||
                provider.location.toLowerCase().includes(query) ||
                (provider.email && provider.email.toLowerCase().includes(query)) ||
                (provider.barcodeId && provider.barcodeId.toLowerCase().includes(query))
              );
            }).length} of {providers.length} providers
          </p>
        )}
      </div>

      {loading ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            animation: 'spin 1s linear infinite'
          }}>
            ⏳
          </div>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            Loading providers from Azure Table Storage...
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            This may take a few seconds
          </p>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{
                backgroundColor: '#2c3e50',
                color: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>ID</th>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>Name</th>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>Specialization</th>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>Location</th>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>Status</th>
                <th style={{
                  padding: '18px 15px',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  minWidth: '320px'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {providers.filter(provider => {
                if (!searchQuery.trim()) {
                  return true;
                }
                const query = searchQuery.toLowerCase();
                return (
                  provider.id.toLowerCase().includes(query) ||
                  provider.name.toLowerCase().includes(query) ||
                  provider.specialization.toLowerCase().includes(query) ||
                  provider.location.toLowerCase().includes(query) ||
                  (provider.email && provider.email.toLowerCase().includes(query)) ||
                  (provider.barcodeId && provider.barcodeId.toLowerCase().includes(query))
                );
              }).map((provider, index) => (
              <tr
                key={provider.id}
                style={{
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                  borderBottom: '1px solid #e8e8e8',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f7ff';
                  e.currentTarget.style.transform = 'scale(1.01)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(52, 152, 219, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <td style={{
                  padding: '16px 15px',
                  fontSize: '13px',
                  color: '#666',
                  fontFamily: 'monospace'
                }}>{provider.id}</td>
                <td style={{ padding: '16px 15px' }}>
                  <span
                    onClick={() => handleViewDetails(provider)}
                    style={{
                      fontWeight: '600',
                      color: '#3498db',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      borderBottom: '2px solid transparent',
                      transition: 'all 0.2s ease',
                      display: 'inline-block',
                      fontSize: '15px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#2980b9';
                      e.target.style.borderBottomColor = '#2980b9';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#3498db';
                      e.target.style.borderBottomColor = 'transparent';
                    }}
                  >
                    {provider.name}
                  </span>
                </td>
                <td style={{
                  padding: '16px 15px',
                  color: '#555',
                  fontSize: '14px'
                }}>{provider.specialization}</td>
                <td style={{
                  padding: '16px 15px',
                  color: '#555',
                  fontSize: '14px'
                }}>{provider.location}</td>
                <td style={{ padding: '16px 15px' }}>
                  <span style={{
                    backgroundColor: '#d5f4e6',
                    color: '#27ae60',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-block',
                    boxShadow: '0 2px 4px rgba(39, 174, 96, 0.2)'
                  }}>
                    {provider.status}
                  </span>
                </td>
                <td style={{ padding: '16px 15px', textAlign: 'center' }}>
                  <div style={{
                    display: 'flex',
                    gap: '6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'nowrap'
                  }}>
                    <button
                      onClick={() => handleViewDetails(provider)}
                      style={{
                        backgroundColor: '#9b59b6',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(155, 89, 182, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#8e44ad';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(155, 89, 182, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#9b59b6';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(155, 89, 182, 0.3)';
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(provider)}
                      style={{
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#2980b9';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(52, 152, 219, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#3498db';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(52, 152, 219, 0.3)';
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleVisibility(provider)}
                      style={{
                        backgroundColor: provider.isHidden ? '#27ae60' : '#f39c12',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {provider.isHidden ? 'Show' : 'Hide'}
                    </button>
                    <button
                      onClick={() => handleDelete(provider.id)}
                      style={{
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#c0392b';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(231, 76, 60, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#e74c3c';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.3)';
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Provider Details Modal */}
      {showDetailsModal && selectedProvider && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              backgroundColor: '#e8f5e9',
              border: '3px solid #27ae60',
              borderRadius: '10px 10px 0 0',
              padding: '30px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>✓</div>
              <h1 style={{
                color: '#27ae60',
                margin: '0 0 10px 0',
                fontSize: '32px'
              }}>
                Verified Provider
              </h1>
              <p style={{ color: '#555', fontSize: '16px', margin: '10px 0' }}>
                This provider has been verified by GLOBAL RESEARCH ORGANISATION
              </p>
              {selectedProvider.barcodeId && (
                <div style={{
                  marginTop: '15px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  fontFamily: 'monospace',
                  letterSpacing: '1px'
                }}>
                  ID: {selectedProvider.barcodeId}
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div style={{ padding: '30px' }}>
              <h2 style={{
                color: '#2c3e50',
                marginTop: 0,
                marginBottom: '20px',
                fontSize: '28px',
                borderBottom: '3px solid #3498db',
                paddingBottom: '12px'
              }}>
                {selectedProvider.name}
              </h2>

              {/* QR Code Section */}
              {selectedProvider.id && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #3498db',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '25px',
                  textAlign: 'center'
                }}>
                  <h3 style={{
                    color: '#2c3e50',
                    fontSize: '18px',
                    marginTop: 0,
                    marginBottom: '15px',
                    fontWeight: '600'
                  }}>
                    Scan QR Code to View Provider Details
                  </h3>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    display: 'inline-block',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <QRCodeSVG
                      value={`${process.env.REACT_APP_BASE_URL || 'http://localhost:3000'}?provider=${selectedProvider.id}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <p style={{
                    marginTop: '15px',
                    marginBottom: 0,
                    fontSize: '12px',
                    color: '#999',
                    fontFamily: 'monospace'
                  }}>
                    Provider ID: {selectedProvider.id}
                  </p>
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
                marginBottom: '25px'
              }}>
                <div>
                  <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Specialization
                  </h3>
                  <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                    {selectedProvider.specialization}
                  </p>
                </div>

                <div>
                  <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Location
                  </h3>
                  <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                    {selectedProvider.location}
                  </p>
                </div>

                <div>
                  <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Founded
                  </h3>
                  <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                    {selectedProvider.founded}
                  </p>
                </div>

                <div>
                  <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Experience
                  </h3>
                  <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                    {selectedProvider.completedProjects}
                  </p>
                </div>

                <div>
                  <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Status
                  </h3>
                  <p style={{ color: '#27ae60', fontSize: '16px', margin: 0, fontWeight: 'bold' }}>
                    {selectedProvider.status}
                  </p>
                </div>

                {selectedProvider.verifiedDate && (
                  <div>
                    <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Verified Date
                    </h3>
                    <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                      {selectedProvider.verifiedDate}
                    </p>
                  </div>
                )}

                {selectedProvider.rating && (
                  <div>
                    <h3 style={{ color: '#555', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Rating
                    </h3>
                    <p style={{ color: '#2c3e50', fontSize: '16px', margin: 0, fontWeight: '500' }}>
                      {selectedProvider.rating}
                    </p>
                  </div>
                )}
              </div>

              {/* Subscription Details Section */}
              {selectedProvider.paymentDate && selectedProvider.subscriptionDuration && (
                <div style={{
                  marginTop: '25px',
                  padding: '20px',
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #2196f3',
                  borderRadius: '8px'
                }}>
                  <h3 style={{
                    margin: '0 0 15px 0',
                    color: '#1976d2',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Subscription Details
                  </h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>
                        Active Date (Payment Date):{' '}
                      </span>
                      <span style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '14px' }}>
                        {new Date(selectedProvider.paymentDate).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>
                        Subscription Plan:{' '}
                      </span>
                      <span style={{ color: '#2196f3', fontWeight: 'bold', fontSize: '14px' }}>
                        {selectedProvider.subscriptionDuration} Year{parseInt(selectedProvider.subscriptionDuration) > 1 ? 's' : ''}
                      </span>
                    </div>
                    {(() => {
                      const subscriptionDetails = calculateSubscriptionDetails(
                        selectedProvider.paymentDate,
                        selectedProvider.subscriptionDuration
                      );
                      return (
                        <>
                          {subscriptionDetails.expiryDate && (
                            <div>
                              <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>
                                Expiry Date:{' '}
                              </span>
                              <span style={{ color: '#f39c12', fontWeight: 'bold', fontSize: '14px' }}>
                                {subscriptionDetails.expiryDate}
                              </span>
                            </div>
                          )}
                          {subscriptionDetails.remainingDays !== null && (
                            <div>
                              <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>
                                Remaining Days:{' '}
                              </span>
                              <span style={{
                                color: subscriptionDetails.remainingDays < 30 ? '#e74c3c' :
                                       subscriptionDetails.remainingDays < 90 ? '#f39c12' : '#27ae60',
                                fontWeight: 'bold',
                                fontSize: '15px'
                              }}>
                                {subscriptionDetails.remainingDays > 0 ? subscriptionDetails.remainingDays : 0} days
                                {subscriptionDetails.remainingDays <= 0 && ' (EXPIRED)'}
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                marginTop: '25px'
              }}>
                <h3 style={{
                  color: '#2c3e50',
                  marginTop: 0,
                  marginBottom: '15px',
                  fontSize: '18px'
                }}>
                  Contact Information
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  <div>
                    <strong style={{ color: '#555' }}>Email:</strong>
                    <br />
                    <a href={`mailto:${selectedProvider.email}`} style={{ color: '#3498db', textDecoration: 'none' }}>
                      {selectedProvider.email}
                    </a>
                  </div>
                  <div>
                    <strong style={{ color: '#555' }}>Phone:</strong>
                    <br />
                    <span style={{ color: '#2c3e50' }}>{selectedProvider.phone}</span>
                  </div>
                  {selectedProvider.website && (
                    <div>
                      <strong style={{ color: '#555' }}>Website:</strong>
                      <br />
                      <a href={selectedProvider.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db', textDecoration: 'none' }}>
                        {selectedProvider.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '30px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => {
                    closeDetailsModal();
                    handleEdit(selectedProvider);
                  }}
                  style={{
                    backgroundColor: '#3498db',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Edit Provider
                </button>
                <button
                  onClick={async () => {
                    const providerId = selectedProvider.id;
                    closeDetailsModal();
                    await handleDelete(providerId);
                  }}
                  style={{
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Delete Provider
                </button>
                <button
                  onClick={closeDetailsModal}
                  style={{
                    backgroundColor: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProviderManager;
